import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { GoogleMapsModule } from '@angular/google-maps';
import { MatCardModule } from "@angular/material/card";
import { StreetInfoService } from '../../shared/services/street-info.service';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { GeoJsonLayer, TextLayer } from '@deck.gl/layers';
import { environment } from '../../../environments/environment';
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

declare var google: any;

@Component({
  selector: 'app-map',
  imports: [GoogleMapsModule, MatCardModule],
  templateUrl: './map.html',
  styleUrl: './map.scss',
  standalone: true
})
export class Map implements OnInit {
  public isMapLoaded: boolean = false;
  public zoom = 8;
  public center: google.maps.LatLngLiteral = {
    lat: 46.950075,
    lng: 11.454337
  };
  public mapOptions: google.maps.MapOptions = {};
  private trafficLayer: any = null;  // Layer del traffico
  private infoWindow: any = null;
  private readonly ETTOMETRICA_LAYERS_ZOOM_THRESHOLD = 16;
  private geojsonData: any = null;
  private isEttometricaLayerVisibile: boolean = true;
  private readonly overlay: any = new GoogleMapsOverlay({
    layers: []
  });

  constructor(
    private readonly streetInfoService: StreetInfoService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    setOptions({
      key: environment.google.googleMapsApiKey,
      v: 'weekly',
      libraries: ['maps', 'marker', 'geometry']
    });

    importLibrary('maps').then(() => {
      this.mapOptions = {
        mapTypeControl: true,
        streetViewControl: true,
        clickableIcons: false,
        mapId: environment.google.googleMapsMapId, // Stile mappa definito nel progetto GCP
        mapTypeControlOptions: {
          position: google.maps.ControlPosition.TOP_RIGHT
        }
      };
      this.infoWindow = new google.maps.InfoWindow();
      this.trafficLayer = new google.maps.TrafficLayer();
      this.isMapLoaded = true;
      this.cdr.detectChanges();
    }).catch((error: any) => {
      console.error('Errore durante il caricamento di Google Maps', error);
    });
  }

  async onMapInitialized(map: google.maps.Map) {
    setTimeout(async () => {
      this.streetInfoService.setMap(map);

      map.addListener('idle', () => {
        this.aggiornaLayer(map);
      });

      let isTrafficVisible: boolean = false;
      const trafficButton = document.createElement("button");

      trafficButton.textContent = "Traffico";
      trafficButton.classList.add("custom-map-control-traffic");

      trafficButton.addEventListener("click", () => {
        if(isTrafficVisible) {
          this.trafficLayer.setMap(null);
          trafficButton.style.fontWeight = "normal";
        } else {
          this.trafficLayer.setMap(map);
          trafficButton.style.fontWeight = "bold";
        }

        isTrafficVisible = !isTrafficVisible;
      });

      map.controls[google.maps.ControlPosition.TOP_RIGHT].push(trafficButton);

      // Interruzioni stradali
      let isDisruptionsVisible: boolean = true;

      const textContainerDisruptions = document.createElement("div");

      textContainerDisruptions.innerHTML = "Interruzioni";
      textContainerDisruptions.style.fontWeight = "bold";

      const svgIconDisruptions = `
        <svg width="22" height="22" viewBox="0 0 22 22" style="display: block;">
          <path d="M10 2L1 18h18L10 2zm1 14H9v-2h2v2zm0-4H9V8h2v4z" fill="#F29900" stroke="#ffffff" stroke-width="1" />
        </svg>`;

      const iconContainerDisruptions = document.createElement("div");

      iconContainerDisruptions.innerHTML = svgIconDisruptions;

      const disruptionsButtonContent = document.createElement("div");

      disruptionsButtonContent.style.display = "flex";
      disruptionsButtonContent.style.alignItems = "center";
      disruptionsButtonContent.appendChild(iconContainerDisruptions);
      disruptionsButtonContent.appendChild(textContainerDisruptions);

      const disruptionsButton = document.createElement("button");

      disruptionsButton.classList.add("custom-map-control-ettometrica");
      disruptionsButton.appendChild(disruptionsButtonContent);
      disruptionsButton.addEventListener("click", () => {
        isDisruptionsVisible = !isDisruptionsVisible;
        textContainerDisruptions.style.fontWeight = isDisruptionsVisible ? "bold" : "normal";
        google.maps.event.trigger(map, 'disruptions_toggled', isDisruptionsVisible);
      });

      map.controls[google.maps.ControlPosition.TOP_RIGHT].push(disruptionsButton);

      // Layer Ettometrica
      const textContainer = document.createElement("div");

      textContainer.innerHTML = "Ettometrica";
      textContainer.style.fontWeight = "bold";

      const svgIcon = `
        <svg width="20" height="20" viewBox="0 0 20 20" style="display: block;">
          <circle cx="10" cy="10" r="7" fill="#EA4335" stroke="#ffffff" stroke-width="2" />
        </svg>`;

      const iconContainer = document.createElement("div");

      iconContainer.innerHTML = svgIcon;

      const ettometricaButtonContent = document.createElement("div");

      ettometricaButtonContent.style.display = "flex";
      ettometricaButtonContent.style.alignItems = "center";

      ettometricaButtonContent.appendChild(iconContainer);
      ettometricaButtonContent.appendChild(textContainer);

      const ettometricaButton = document.createElement("button");

      ettometricaButton.classList.add("custom-map-control-ettometrica");

      ettometricaButton.appendChild(ettometricaButtonContent);

      ettometricaButton.addEventListener("click", () => {
        this.isEttometricaLayerVisibile = !this.isEttometricaLayerVisibile;

        if(this.isEttometricaLayerVisibile) {
          textContainer.style.fontWeight = "bold";
        } else {
          textContainer.style.fontWeight = "normal";
        }

        this.aggiornaLayer(map);
      });

      map.controls[google.maps.ControlPosition.TOP_RIGHT].push(ettometricaButton);

      // Caricamento layer Ettometrica
      this.overlay.setMap(map);

      const response = await fetch('assets/geojson/Ettometrica.geojson');

      this.geojsonData = await response.json();
      this.aggiornaLayer(map);
    }, 0);
  }

  aggiornaLayer(map: any) {
    if(!this.geojsonData) {
      return;
    }

    const zoomAttuale = map.getZoom() || 0;
    const zoomSufficiente = zoomAttuale >= this.ETTOMETRICA_LAYERS_ZOOM_THRESHOLD;
    const layerVisibili = this.isEttometricaLayerVisibile && zoomSufficiente;

    if(!layerVisibili) {
      this.infoWindow.close();
    }

    const geoJsonLayer = new GeoJsonLayer({
      id: 'geojson-data',
      data: this.geojsonData,
      visible: layerVisibili,
      pointType: 'circle',
      pointRadiusUnits: 'pixels',
      getPointRadius: 5,
      getFillColor: [234, 67, 53, 255],
      getLineColor: [255, 255, 255, 255],
      stroked: true,
      getLineWidth: 2,
      lineWidthUnits: 'pixels',
      pickable: true,
      onClick: (info) => {
        if(info.object && info.coordinate) {
          const properties = info.object.properties;
          let htmlContent = '<div style="color: #333; max-height: 250px; max-width: 400px; overflow-y: auto; padding-right: 10px; font-size: 14px;">';

          for(const [key, value] of Object.entries(properties)) {
            htmlContent += `<div style="margin-bottom: 4px;"><strong>${key}:</strong> ${value}</div>`;
          }

          htmlContent += '</div>';

          this.infoWindow.setContent(htmlContent);
          this.infoWindow.setPosition({
            lat: info.coordinate[1],
            lng: info.coordinate[0]
          });
          this.infoWindow.open(map);
        }
      }
    });

    const textLayer = new TextLayer({
      id: 'label',
      data: this.geojsonData.features,
      visible: layerVisibili,
      getPosition: d => d.geometry.coordinates,
      getText: d => d.properties.Name ? String(d.properties.Name) : '',
      getSize: 14,
      fontWeight: '500',
      getColor: [32, 33, 36, 255],
      getPixelOffset: [0, -22],
      fontFamily: '"Google Sans", "Roboto", Arial, sans-serif',
      background: true,
      getBackgroundColor: [255, 255, 255, 230],
      backgroundPadding: [6, 4],
      getBorderColor: [218, 220, 224, 255],
      getBorderWidth: 1
    });

    this.overlay.setProps({
      layers: [geoJsonLayer, textLayer]
    });
  }
}
